import { useState } from "react";
import type { CoachingNote } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import {
  FeedbackThumb,
  FeedbackTonePill,
  ObservationThumb,
  noteTitle,
} from "./components/FeedbackCardBits";
import { BoardChip, boardHasContent } from "./components/BoardChip";

/**
 * `/feedback` Notes tab (M7) — the viewer's coaching notes as
 * compact cards. Ports legacy `renderFeedbackNotes`
 * (`js/coaching/feedback.js`).
 *
 * Cards are intentionally compact — thumb + tone + title + meta.
 * No inline summary, no inline board, no action-button row.
 * Clicking a card opens the M5 `<FeedbackPlayerModal>` in `note`
 * mode (video OR observation/board) — the single review surface,
 * so a viewer always lands in one consistent UI.
 *
 * Privacy: the `notes[]` are already viewer-filtered +
 * `coach_private_note`-scrubbed server-side. This component never
 * templates `coach_private_note` — defense in depth.
 */

export type FeedbackNotesTabProps = {
  notes: CoachingNote[];
  /** note_ids the viewer has marked reviewed (from `bundle.reviews`). */
  reviewedNoteIds: Set<number>;
};

export function FeedbackNotesTab({
  notes,
  reviewedNoteIds,
}: FeedbackNotesTabProps) {
  const [openNote, setOpenNote] = useState<CoachingNote | null>(null);

  if (notes.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm px-3 py-6"
        data-testid="feedback-notes-empty"
      >
        No coaching notes have been shared with you yet.
      </p>
    );
  }

  return (
    <>
      <ul
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}
        data-testid="feedback-notes-list"
      >
        {notes.map((note) => (
          <NoteCard
            key={note.id}
            note={note}
            reviewed={reviewedNoteIds.has(note.id)}
            onOpen={() => setOpenNote(note)}
          />
        ))}
      </ul>

      {/* The M5 focused player — note mode. The note carries its
       * own match_id + team_id, so `match` is built straight from
       * it. */}
      {openNote ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: openNote }}
          match={{
            id: openNote.match_id ?? "",
            team_id: openNote.team_id,
          }}
          onClose={() => setOpenNote(null)}
        />
      ) : null}
    </>
  );
}

function NoteCard({
  note,
  reviewed,
  onOpen,
}: {
  note: CoachingNote;
  reviewed: boolean;
  onOpen: () => void;
}) {
  const isObservation = (note.note_context ?? "video") === "observation";
  const title = noteTitle(note);

  // Meta line — observation notes have no match/slot/timestamp
  // anchor, so use event metadata (never "null · 0:00").
  const metaParts: string[] = [];
  if (isObservation) {
    if (note.event_type) {
      const t = note.event_type;
      metaParts.push(`${t[0].toUpperCase()}${t.slice(1)} observation`);
    } else {
      metaParts.push("Observation");
    }
    if (note.event_date) metaParts.push(note.event_date);
  } else {
    if (note.match_id) metaParts.push(note.match_id);
    metaParts.push(formatPlayhead(note.timestamp_seconds));
  }
  metaParts.push(reviewed ? "Reviewed" : "New");

  return (
    <li>
      <article
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        data-testid="feedback-note-card"
        data-note-context={isObservation ? "observation" : "video"}
        aria-label={`Open ${title}`}
        className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-rule bg-bg-card px-3 py-2 text-left transition-colors hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
      >
        {/* Observation thumb variants (legacy parity):
         *   - board-bearing: a chip-sized SVG preview of the
         *     tactical board, so it's distinguishable from plain
         *     observations on the list.
         *   - no board: the generic clipboard glyph.
         * Video notes get the auth-fetched JPEG thumb. */}
        {isObservation ? (
          boardHasContent(note.tactical_board_json) ? (
            <BoardChip raw={note.tactical_board_json} />
          ) : (
            <ObservationThumb />
          )
        ) : (
          <FeedbackThumb kind="note" id={note.id} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <FeedbackTonePill noteType={note.note_type} />
            {reviewed ? (
              <span
                className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
                data-testid="feedback-note-reviewed"
              >
                Reviewed ✓
              </span>
            ) : null}
          </div>
          <div className="font-display text-ink truncate mt-0.5">
            {title}
          </div>
          <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
            {metaParts.join(" · ")}
          </div>
        </div>
      </article>
    </li>
  );
}
